import InfoIcon from "@mui/icons-material/Info";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, type DatePickerSlotProps } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useMutation } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useMemo, useState, type FC } from "react";
import { queryClient } from "src/query-client";
import theme from "src/styles/theme";
import { EMPTY_ARRAY, EMPTY_MAP, EMPTY_SET } from "src/utils/empty-values";
import { parseMaybeDateString } from "src/utils/parse-maybe-date-string";
import { showErrorToast, showSuccessToast } from "src/utils/toast";
import { ServerScopeName, ServerVendorAccount } from "../api-v2/types/server-vendor-account";
import { sunsetVendorAccounts } from "../api-v2/vendor-accounts";
import css from "src/styles/dialog.module.css";

interface ParsedScopeInfo {
  name: string;
  activationDate: Dayjs | null;
  deactivationDate: Dayjs | null;
}

const scopeNameLabels: Record<ServerScopeName, string> = {
  [ServerScopeName.PRICE_CLAIMS]: "Price Claims",
  [ServerScopeName.SHORTAGES]: "Shortages",
  [ServerScopeName.CHARGEBACKS]: "Chargebacks",
  [ServerScopeName.ACCRUALS]: "Accruals",
  [ServerScopeName.VENDOR_RETURNS]: "Vendor Returns",
};

const datePickerSlotProps: DatePickerSlotProps<Dayjs, true> = {
  field: { clearable: true },
  textField: { variant: "standard", fullWidth: true },
};

export interface SunsetDialogProps {
  stores: ServerVendorAccount[];
  onSuccess: () => void;
  onClose: () => void;
}

export const SunsetStoreDialog: FC<SunsetDialogProps> = ({ stores = EMPTY_ARRAY, onSuccess, onClose }) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [notes, setNotes] = useState("");
  const mutation = useMutation(
    {
      mutationKey: ["sunset-store"],
      async mutationFn(sunsetDate: Dayjs | null) {
        if (!sunsetDate?.isValid()) {
          throw new Error("Invalid sunset date selected");
        }
        const trimmedNotes = notes.trim();
        return sunsetVendorAccounts({
          accountIds: stores.map(store => store.ID),
          sunsetDate: sunsetDate.format("YYYY-MM-DD"),
          notes: trimmedNotes || undefined,
        });
      },
      onSuccess() {
        showSuccessToast("Store sunset successfully");
        onSuccess();
        onClose();
      },
      onError(error) {
        console.error("Failed to sunset store:", error);
        showErrorToast(`Failed to sunset store - ${error.message}`);
      },
    },
    // The dependency we use for confirmation dialogs, `react-confirm-alert`, wraps our custom UI inside a
    // separate React context. We must pass the query client manually.
    queryClient,
  );

  const parsedStoreScopes: Map<ServerVendorAccount, ParsedScopeInfo[]> = useMemo(() => {
    const result = new Map<ServerVendorAccount, ParsedScopeInfo[]>();
    for (const store of stores) {
      result.set(
        store,
        store.SCOPES?.map(scope => ({
          name: scopeNameLabels[scope.scope_name] ?? scope.scope_name,
          activationDate: scope.activation_date ? dayjs(parseMaybeDateString(scope.activation_date)) : null,
          deactivationDate: scope.deactivation_date ? dayjs(parseMaybeDateString(scope.deactivation_date)) : null,
        })) ?? EMPTY_ARRAY,
      );
    }
    return result;
  }, [stores]);

  const {
    hasValidDateSelection,
    affectedScopeCount,
    unaffectedScopeCount,
    affectedScopes,
    unaffectedScopes,
    unaffectedStores,
  } = useScopeSunsetOutcome(parsedStoreScopes, selectedDate);

  const isSubmitDisabled =
    !hasValidDateSelection || !affectedScopeCount || unaffectedStores.size > 0 || mutation.isPending;

  /*
   * The dependency we use for confirmation dialogs, `react-confirm-alert`, wraps our custom UI inside a
   * separate React context. This means we have to re-setup all necessary providers
   * (like `ThemeProvider` and `LocalizationProvider`) inside the custom UI component.
   */

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className={css.dialog}>
          <div className={css.content}>
            <p className={css.title}>
              {stores.length === 1 ? (
                <>Are you sure you want to sunset the store "{stores[0].NAME}"?</>
              ) : (
                <>Are you sure you want to sunset the selected {stores.length} stores?</>
              )}
            </p>
            <p style={{ marginBottom: "1rem" }}>
              All active scopes for {stores.length === 1 ? <>this store</> : <>the selected stores</>} will be
              deactivated. The system and assigned Recovery Specialists will continue to monitor{" "}
              {stores.length === 1 ? <>this store</> : <>these stores</>} for updates to existing work packets and
              cases.
            </p>
            <DatePicker
              label={
                stores.length === 1 ? "Sunset this store starting from..." : "Sunset these stores starting from..."
              }
              format="YYYY-MM-DD"
              value={selectedDate}
              onChange={newValue => setSelectedDate(newValue)}
              slotProps={datePickerSlotProps}
              disabled={mutation.isPending}
            />
            <TextField
              label="Notes"
              multiline
              variant="standard"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              disabled={mutation.isPending}
              sx={{ marginBottom: "1rem" }}
            />
          </div>

          {hasValidDateSelection && (
            <Alert
              icon={<InfoIcon fontSize="inherit" />}
              severity={affectedScopeCount === 0 || unaffectedStores.size > 0 ? "warning" : "info"}
              style={{ marginBottom: "1rem" }}
            >
              <div style={{ marginBottom: "1rem" }}>
                {affectedScopeCount === 0 ? (
                  <p>All scopes are unaffected by the selected sunset date.</p>
                ) : stores.length === 1 ? (
                  <>
                    <p>
                      The following scopes will be set to deactivate on <b>{selectedDate?.format("MMM D, YYYY")}</b>:
                    </p>
                    <ul>
                      <li>{[...affectedScopes.values()][0].join(", ")}</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p>
                      {stores.length === 1 ? <>This scope</> : <>The following scopes</>} across {affectedScopes.size}{" "}
                      {affectedScopes.size === 1 ? <>store</> : <>stores</>} will be set to deactivate on{" "}
                      <b>{selectedDate?.format("MMM D, YYYY")}</b>:
                    </p>
                    <ul>
                      {[...affectedScopes.entries()].map(([store, scopeNames]) => (
                        <li key={store.ID}>
                          <b>{store.NAME}</b>
                          <ul>
                            <li>{scopeNames.join(", ")}</li>
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              {unaffectedScopes.size > 0 && (
                <>
                  <p>The following {unaffectedScopeCount === 1 ? <>scope</> : <>scopes</>} will not be affected:</p>
                  {stores.length === 1 ? (
                    <ul>
                      {[...unaffectedScopes.values()][0].map(({ scopeName, reason }) => (
                        <li key={scopeName}>
                          <b>{scopeName}</b> - {reason}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul>
                      {[...unaffectedScopes.entries()].map(([store, unaffectedStoreScopes]) => (
                        <li key={store.ID}>
                          <p>
                            {store.NAME} {unaffectedStores.has(store) && <b>(completely unaffected)</b>}
                          </p>
                          <ul>
                            {unaffectedStoreScopes.map(({ scopeName, reason }) => (
                              <li key={scopeName}>
                                <b>{scopeName}</b> - {reason}
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              {stores.length > 1 && unaffectedStores.size > 0 && (
                <p>
                  {unaffectedStores.size === 1 ? (
                    <>One store is completely unaffected.</>
                  ) : (
                    <>Multiple stores are completely unaffected.</>
                  )}{" "}
                  <span>Check the date and store selection is correct.</span>
                </p>
              )}
            </Alert>
          )}

          <div className={css.buttonGroup}>
            <Button onClick={onClose} className={css.button} disabled={mutation.isPending} variant="text">
              Cancel
            </Button>
            <Button
              onClick={() => mutation.mutate(selectedDate)}
              className={css.button}
              variant="contained"
              color="primary"
              disabled={isSubmitDisabled}
              type="submit"
            >
              Sunset {stores.length === 1 ? <>this store</> : <>selected stores</>}
              {mutation.isPending && <CircularProgress size={14} style={{ marginLeft: "12px" }} />}
            </Button>
          </div>
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

interface ScopeOutcomes {
  hasValidDateSelection: boolean;
  affectedScopeCount: number;
  unaffectedScopeCount: number;
  affectedScopes: Map<ServerVendorAccount, string[]>;
  unaffectedScopes: Map<ServerVendorAccount, { scopeName: string; reason: string }[]>;
  unaffectedStores: Set<ServerVendorAccount>;
}

const fallbackScopeOutcomes: ScopeOutcomes = {
  hasValidDateSelection: false,
  affectedScopeCount: 0,
  unaffectedScopeCount: 0,
  affectedScopes: EMPTY_MAP,
  unaffectedScopes: EMPTY_MAP,
  unaffectedStores: EMPTY_SET,
};

function computeScopeSunsetOutcome(
  storeScopes: Map<ServerVendorAccount, ParsedScopeInfo[]>,
  sunsetDate: Dayjs | null,
): ScopeOutcomes {
  if (!sunsetDate?.isValid()) return fallbackScopeOutcomes;

  let affectedScopeCount = 0;
  let unaffectedScopeCount = 0;

  const unaffectedStores = new Set<ServerVendorAccount>();
  const affectedScopes = new Map<ServerVendorAccount, string[]>();
  const unaffectedScopes = new Map<ServerVendorAccount, { scopeName: string; reason: string }[]>();

  for (const [store, scopes] of storeScopes) {
    const storeAffectedScopes: string[] = [];
    const storeUnaffectedScopes: { scopeName: string; reason: string }[] = [];

    for (const scope of scopes) {
      if (scope.deactivationDate) {
        storeUnaffectedScopes.push({
          scopeName: scope.name,
          reason: `this scope already has a deactivation date (${scope.deactivationDate.format("MMM D, YYYY")})`,
        });
      } else if (scope.activationDate?.isAfter(sunsetDate)) {
        storeUnaffectedScopes.push({
          scopeName: scope.name,
          reason: `the selected date happens before scope's activation date (${scope.activationDate.format("MMM D, YYYY")})`,
        });
      } else {
        storeAffectedScopes.push(scope.name);
      }
    }

    affectedScopeCount += storeAffectedScopes.length;
    unaffectedScopeCount += storeUnaffectedScopes.length;

    if (storeAffectedScopes.length > 0) {
      affectedScopes.set(store, storeAffectedScopes);
    }
    if (storeUnaffectedScopes.length > 0) {
      unaffectedScopes.set(store, storeUnaffectedScopes);
    }
    if (storeUnaffectedScopes.length === scopes.length) {
      unaffectedStores.add(store);
    }
  }
  return {
    hasValidDateSelection: true,
    affectedScopeCount,
    unaffectedScopeCount,
    affectedScopes,
    unaffectedScopes,
    unaffectedStores,
  };
}

function useScopeSunsetOutcome(
  storeScopes: Map<ServerVendorAccount, ParsedScopeInfo[]>,
  sunsetDate: Dayjs | null,
): ScopeOutcomes {
  return useMemo(() => computeScopeSunsetOutcome(storeScopes, sunsetDate), [storeScopes, sunsetDate]);
}
